import React, { useEffect, useMemo, useState } from 'react';
import TimeSeriesChart from './TimeSeriesChart';
import { fetchDeviceSnapshotMetadata, fetchHealthScore } from '../api/restapi';
import { formatLocalDateTime } from '../utils/formatDateTime';
import {
    AREA_LINE_STYLES,
    SCORE_AREA_LABELS,
    SCORE_SERIES_COLORS,
    SeriesActionButtons,
    SeriesControls,
    getDefaultVisibleSeriesKeys,
} from './ScoreTrendSeries';

function normalizedTimestamp(value) {
    return value && !/[zZ]|[+-]\d\d:\d\d$/.test(value) ? `${value}Z` : value;
}

const DEFAULT_SCORING_WINDOW_HOURS = 6;
const EPISODE_ADJACENCY_TOLERANCE_MS = 60 * 1000;

function scoringWindowHours(window) {
    const hours = Number(window?.windowLengthHour);
    return Number.isFinite(hours) && hours > 0 ? hours : DEFAULT_SCORING_WINDOW_HOURS;
}

function scoringWindowStart(window) {
    return new Date(normalizedTimestamp(window?.windowStart));
}

function scoringWindowEnd(window) {
    const start = scoringWindowStart(window);
    return new Date(start.getTime() + scoringWindowHours(window) * 60 * 60 * 1000);
}

function formatScoringWindow(window) {
    if (!window?.windowStart) return 'Unavailable';
    const start = scoringWindowStart(window);
    const end = scoringWindowEnd(window);
    return `${formatLocalDateTime(start.toISOString())} – ${formatLocalDateTime(end.toISOString())}`;
}

function formatEpisodeDuration(startWindow, end) {
    const durationHours = Math.round((end.getTime() - scoringWindowStart(startWindow).getTime()) / (60 * 60 * 1000));
    return `${durationHours} ${durationHours === 1 ? 'hour' : 'hours'}`;
}

export function scoreStatus(score) {
    if (score == null) return 'UNKNOWN';
    if (score < 0.5) return 'CRITICAL';
    if (score < 0.75) return 'DEGRADED';
    return 'STABLE';
}

export function buildDeviceSeries(windows) {
    const areas = new Map();
    const overall = {
        key: 'device', name: 'Device Score', color: SCORE_SERIES_COLORS[0],
        data: windows.map((window) => ({ x: normalizedTimestamp(window.windowStart), y: window.scoreRaw == null ? null : window.scoreRaw * 100 })),
    };
    windows.forEach((window) => (window.domains || []).forEach((domain) => (domain.subdomains || []).forEach((area) => {
        const key = (area.subDomainLabel || '').toLowerCase();
        if (!areas.has(key)) areas.set(key, []);
        areas.get(key).push({ x: normalizedTimestamp(window.windowStart), y: area.scoreRaw == null ? null : area.scoreRaw * 100 });
    })));
    return [overall, ...[...areas.entries()].map(([key, data], index) => ({
        key: `area:${key}`,
        name: SCORE_AREA_LABELS[key.toUpperCase()] || key.toUpperCase(),
        color: SCORE_SERIES_COLORS[(index + 1) % SCORE_SERIES_COLORS.length],
        ...AREA_LINE_STYLES[index % AREA_LINE_STYLES.length],
        data,
    }))];
}

export function buildProblematicEpisodes(windows, areaColors = new Map()) {
    const windowsByArea = new Map();
    [...windows]
        .sort((left, right) => scoringWindowStart(left).getTime() - scoringWindowStart(right).getTime())
        .forEach((window) => (window.domains || []).forEach((domain) => (domain.subdomains || []).forEach((area) => {
            const status = scoreStatus(area.scoreRaw);
            if (!['CRITICAL', 'DEGRADED'].includes(status)) return;
            const key = (area.subDomainLabel || '').toLowerCase();
            if (!key) return;
            if (!windowsByArea.has(key)) windowsByArea.set(key, []);
            windowsByArea.get(key).push({ ...area, window, status });
        })));

    const severityOrder = { CRITICAL: 0, DEGRADED: 1 };
    const episodes = [];
    windowsByArea.forEach((areaWindows, areaKey) => {
        let episode = null;
        areaWindows.forEach((entry) => {
            const startsNewEpisode = !episode
                || scoringWindowStart(entry.window).getTime()
                    > scoringWindowEnd(episode.windows[episode.windows.length - 1].window).getTime()
                        + EPISODE_ADJACENCY_TOLERANCE_MS;
            if (startsNewEpisode) {
                episode = { areaKey, windows: [] };
                episodes.push(episode);
            }
            episode.windows.push(entry);
        });
    });

    return episodes.map((episode) => {
        const worstWindow = episode.windows.reduce((worst, entry) => (
            entry.scoreRaw < worst.scoreRaw ? entry : worst
        ));
        const status = episode.windows.some((entry) => entry.status === 'CRITICAL') ? 'CRITICAL' : 'DEGRADED';
        const firstWindow = episode.windows[0].window;
        const lastWindow = episode.windows[episode.windows.length - 1].window;
        return {
            ...episode,
            key: `${episode.areaKey}:${firstWindow.windowStart}`,
            name: SCORE_AREA_LABELS[episode.areaKey.toUpperCase()] || episode.areaKey.toUpperCase(),
            color: areaColors.get(episode.areaKey),
            status,
            firstWindow,
            lastWindow,
            end: scoringWindowEnd(lastWindow),
            worstWindow,
        };
    }).sort((left, right) => (
        severityOrder[left.status] - severityOrder[right.status]
        || scoringWindowStart(right.worstWindow.window).getTime() - scoringWindowStart(left.worstWindow.window).getTime()
        || (left.worstWindow.scoreRaw ?? 1) - (right.worstWindow.scoreRaw ?? 1)
        || left.name.localeCompare(right.name)
    ));
}

function DeviceIcon() {
    return (
        <div className="device-snapshot-icon" aria-hidden="true">
            <svg viewBox="0 0 48 48"><rect x="10" y="6" width="28" height="36" rx="5" /><path d="M18 11h12M20 36h8" /><circle cx="24" cy="28" r="5" /></svg>
        </div>
    );
}

function DeviceScoreTooltip() {
    return (
        <div className="snapshot-score-tooltip device-snapshot-tooltip">
            <button type="button" className="snapshot-score-tooltip-trigger" aria-label="Explain latest device score" aria-describedby="device-score-tooltip">i</button>
            <div id="device-score-tooltip" className="snapshot-score-tooltip-content" role="tooltip">
                <p><strong>Latest Device Score:</strong> this device’s most recent raw scoring-window score, from 0 to 100. Higher is healthier; no EMA smoothing is applied.</p>
                <p><strong>Calculation:</strong> available health-area scores are combined with configured weights. Metric penalty rules reduce a health-area score; missing telemetry does not count against the score.</p>
                <p><strong>Status:</strong> STABLE is 75–100, DEGRADED is 50–74, and CRITICAL is below 50.</p>
            </div>
        </div>
    );
}

function DeviceProblematicAreasTooltip() {
    return (
        <div className="snapshot-score-tooltip device-snapshot-tooltip">
            <button type="button" className="snapshot-score-tooltip-trigger" aria-label="Explain device Problematic Areas" aria-describedby="device-problematic-areas-tooltip">i</button>
            <div id="device-problematic-areas-tooltip" className="snapshot-score-tooltip-content" role="tooltip">
                <p><strong>Device scope:</strong> each row is a consecutive run of DEGRADED or CRITICAL scoring windows for this device.</p>
                <p>Select a specific scoring window to investigate that event with ±12 hours of device context. These local events can differ from fleet-wide averages.</p>
            </div>
        </div>
    );
}

export default function DeviceSnapshotModal({ device, days, onClose, onInvestigate }) {
    const [metadata, setMetadata] = useState({ data: null, loading: true, error: null });
    const [health, setHealth] = useState({ data: null, loading: true, error: null });
    const [visibleKeys, setVisibleKeys] = useState(new Set());
    const [expandedEpisodes, setExpandedEpisodes] = useState(new Set());

    useEffect(() => {
        const closeOnEscape = (event) => { if (event.key === 'Escape') onClose(); };
        document.addEventListener('keydown', closeOnEscape);
        return () => document.removeEventListener('keydown', closeOnEscape);
    }, [onClose]);

    useEffect(() => {
        let active = true;
        const to = new Date();
        const from = new Date(to.getTime() - days * 86400000);
        const params = {
            systemId: device.systemId,
            startTimestamp: from.toISOString().slice(0, 19),
            endTimestamp: to.toISOString().slice(0, 19),
        };
        Promise.allSettled([fetchDeviceSnapshotMetadata(device.systemId), fetchHealthScore(params)]).then(([metadataResult, healthResult]) => {
            if (!active) return;
            const metadataValue = metadataResult.status === 'fulfilled' ? metadataResult.value : null;
            const healthValue = healthResult.status === 'fulfilled' ? healthResult.value : null;
            setMetadata(metadataValue?.errorMessage || metadataResult.status === 'rejected'
                ? { data: null, loading: false, error: metadataValue?.errorMessage || metadataResult.reason?.message }
                : { data: metadataValue, loading: false, error: null });
            setHealth(healthValue?.errorMessage || healthResult.status === 'rejected'
                ? { data: null, loading: false, error: healthValue?.errorMessage || healthResult.reason?.message }
                : { data: healthValue, loading: false, error: null });
        });
        return () => { active = false; };
    }, [device.systemId, days]);

    const windows = useMemo(() => health.data?.healthScores || [], [health.data]);
    const latestWindow = windows[windows.length - 1] || health.data?.precedingHealthScore || null;
    const latestScore = latestWindow?.scoreRaw ?? device.score;
    const series = useMemo(() => buildDeviceSeries(windows), [windows]);
    const timeRange = days <= 1 ? '24h' : days <= 7 ? '7d' : '30d';
    const status = scoreStatus(latestScore);
    const associatedUser = metadata.loading
        ? 'Loading…'
        : metadata.error
            ? 'Unavailable'
            : metadata.data?.onboarded
                ? metadata.data.onboardedUser || 'Unavailable'
                : 'Not onboarded';

    useEffect(() => {
        setVisibleKeys(getDefaultVisibleSeriesKeys(series));
    }, [series]);

    const toggleSeries = (key) => {
        setVisibleKeys((previous) => {
            if (previous.has(key) && previous.size === 1) return previous;
            const next = new Set(previous);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const selectAllSeries = () => setVisibleKeys(new Set(series.map((item) => item.key)));
    const unselectAllSeries = () => setVisibleKeys(new Set());

    const visibleSeries = series.map((item) => ({
        ...item,
        hidden: !visibleKeys.has(item.key),
    }));
    const problematicEpisodes = useMemo(() => buildProblematicEpisodes(
        windows,
        new Map(series.filter((item) => item.key.startsWith('area:'))
            .map((item) => [item.key.slice('area:'.length), item.color]))
    ), [series, windows]);

    const toggleEpisode = (key) => {
        setExpandedEpisodes((previous) => {
            const next = new Set(previous);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const investigateWindow = (entry) => {
        if (!onInvestigate) return;
        onInvestigate({
            systemId: device.systemId,
            referenceTimestamp: entry.window.windowStart,
            contextWindow: '± 12 hours',
        });
    };

    return (
        <div className="device-snapshot-overlay" role="presentation" onClick={onClose}>
            <div className="device-snapshot-modal" role="dialog" aria-modal="true" aria-labelledby="device-snapshot-title" onClick={(event) => event.stopPropagation()}>
                <header className="device-snapshot-header">
                    <div className="device-snapshot-identity">
                        <DeviceIcon />
                        <div>
                            <div className="device-snapshot-eyebrow">Device Snapshot</div>
                            <h2 id="device-snapshot-title">{device.systemId}</h2>
                            <p>{metadata.data?.deviceTag || device.deviceTag || 'Unassigned device group'}</p>
                        </div>
                    </div>
                    <button type="button" className="device-snapshot-close" onClick={onClose} aria-label="Close device snapshot">×</button>
                </header>

                <div className="device-snapshot-body">
                    <div className="device-snapshot-summary">
                        <section className={`device-snapshot-score-card device-snapshot-score-${status.toLowerCase()}`} aria-label="Latest device score">
                            <div className="device-snapshot-score-heading"><span className="device-snapshot-score-label">Latest Device Score</span><DeviceScoreTooltip /></div>
                            <div className="device-snapshot-score-value">
                                <strong>{latestScore == null ? '—' : Math.round(latestScore * 100)}</strong>
                                <span>/100</span>
                            </div>
                            <span className={`device-snapshot-score-status device-snapshot-status-${status.toLowerCase()}`}>{status}</span>
                            {latestWindow && <span className="device-snapshot-score-window">Per-device scoring window: {formatScoringWindow(latestWindow)}</span>}
                        </section>

                        <section className="device-snapshot-metadata" aria-label="Device details">
                            <div><span>Last registered</span><strong>{metadata.loading ? 'Loading…' : formatLocalDateTime(metadata.data?.lastRegisteredOn) || 'Unavailable'}</strong></div>
                            <div><span>Device group</span><strong>{metadata.data?.deviceTag || device.deviceTag || 'Unassigned'}</strong></div>
                            <div><span>Associated user</span><strong>{associatedUser}</strong></div>
                        </section>
                    </div>

                    {(metadata.error || health.error) && <div className="device-snapshot-warning">Some device details could not be loaded.</div>}

                    <section className="device-snapshot-section">
                        <div className="device-snapshot-section-heading"><h3>Device Health Trends</h3><SeriesActionButtons series={series} visibleKeys={visibleKeys} onSelectAll={selectAllSeries} onUnselectAll={unselectAllSeries} /></div>
                        <p className="device-snapshot-context">Each point is a per-device scoring window within the selected lookback, not a time average.</p>
                        {health.loading ? <div className="device-snapshot-loading">Loading health trends…</div> : (
                            <div className="device-snapshot-trends">
                                <SeriesControls series={series} visibleKeys={visibleKeys} onToggle={toggleSeries} ariaLabel="Device score trend legend" />
                                <TimeSeriesChart title="Device health trends" series={visibleSeries} yAxisLabel="Health score" timeRange={timeRange} timeZone="local" showLegend={false} showTitle={false} chartHeight={300} />
                            </div>
                        )}
                    </section>

                    <section className="device-snapshot-section">
                        <div className="device-snapshot-section-heading"><div className="device-snapshot-heading-with-tooltip"><h3>Problematic Areas</h3><DeviceProblematicAreasTooltip /></div></div>
                        <p className="device-snapshot-context">Investigation anchors are per-device scoring-window episodes. Select an exact window to investigate it with ±12 hours of context.</p>
                        {health.loading ? <div className="device-snapshot-loading">Loading problematic areas…</div> : problematicEpisodes.length === 0 ? (
                            <div className="device-snapshot-empty">No degraded or critical scoring windows detected.</div>
                        ) : (
                            <div className="device-snapshot-episodes">
                                {problematicEpisodes.map((episode) => {
                                    const expanded = expandedEpisodes.has(episode.key);
                                    const worstScore = Math.round((episode.worstWindow.scoreRaw || 0) * 100);
                                    return <article className={`device-snapshot-episode device-snapshot-area-${episode.status.toLowerCase()}`} style={{ '--device-area-color': episode.color }} key={episode.key}>
                                        <div className="device-snapshot-episode-summary">
                                            <div className="device-snapshot-episode-title">
                                                <strong>{episode.name}</strong>
                                                <span className={`device-snapshot-status-${episode.status.toLowerCase()}`}>{episode.status}</span>
                                            </div>
                                            <div className="device-snapshot-episode-details">
                                                <span>First detected <b>{formatLocalDateTime(episode.firstWindow.windowStart)}</b></span>
                                                <span>Worst window <b>{worstScore}/100</b> · {formatLocalDateTime(episode.worstWindow.window.windowStart)}</span>
                                                <span>Duration <b>{formatEpisodeDuration(episode.firstWindow, episode.end)}</b></span>
                                                <span>{episode.windows.length} scoring {episode.windows.length === 1 ? 'window' : 'windows'}</span>
                                            </div>
                                            {episode.worstWindow.metrics?.length > 0 && <div className="device-snapshot-episode-drivers">Drivers: {episode.worstWindow.metrics.map((metric) => `${metric.metricName} (${metric.penaltyLabel})`).join(', ')}</div>}
                                        </div>
                                        <div className="device-snapshot-episode-actions">
                                            <button type="button" className="device-snapshot-window-button" onClick={() => investigateWindow(episode.worstWindow)}>Investigate worst</button>
                                            <button type="button" className="device-snapshot-expand-button" aria-expanded={expanded} onClick={() => toggleEpisode(episode.key)}>{expanded ? 'Hide windows' : `View ${episode.windows.length} ${episode.windows.length === 1 ? 'window' : 'windows'}`}</button>
                                        </div>
                                        {expanded && <div className="device-snapshot-window-list">
                                            {episode.windows.map((entry) => <div className="device-snapshot-window" key={`${episode.key}:${entry.window.windowStart}`}>
                                                <div><strong>{formatScoringWindow(entry.window)}</strong><span className={`device-snapshot-status-${entry.status.toLowerCase()}`}>{entry.status} · {Math.round((entry.scoreRaw || 0) * 100)}/100</span></div>
                                                <button type="button" className="device-snapshot-window-button" onClick={() => investigateWindow(entry)}>Investigate</button>
                                            </div>)}
                                        </div>}
                                    </article>;
                                })}
                            </div>
                        )}
                    </section>
                </div>
            </div>
        </div>
    );
}
